<script setup lang="ts">
/**
 * One search field for the whole catalog — operating systems and apps alike.
 *
 * `/` focuses it from anywhere on the page (`defineShortcuts` ignores the key
 * while another field is being typed in), Escape clears it — and only clears
 * it: inside the reinstall modal the same key would otherwise close the modal
 * and lose the choice being made. The result count is announced politely, so
 * someone who cannot see the grid change still knows what the query did.
 */
const props = defineProps<{
  modelValue: string
  source: 'os' | 'marketplace'
  /** Results for a non-empty query; null while there is none. */
  resultCount: number | null
}>()

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

const { t } = useI18n()
const input = ref<{ inputRef?: HTMLInputElement | null } | null>(null)

// A phone's field shows about thirty characters, and the full hint cut off at
// "WordPre" reads as a rendering fault. Measured rather than a media query:
// the same picker sits in the checkout column and in a much wider modal. An
// unmeasured field (SSR, first render) keeps the full hint, so the server and
// the hydrating client agree; the label a screen reader hears is always full.
const wrapper = ref<HTMLElement | null>(null)
const { width } = useElementSize(wrapper)
const compact = computed(() => width.value > 0 && width.value < 540)

const label = computed(() =>
  props.source === 'marketplace'
    ? t('billing.page.checkout.os.catalog.search.placeholderApps')
    : t('billing.page.checkout.os.catalog.search.placeholderOs')
)
const placeholder = computed(() => {
  if (!compact.value) return label.value
  return props.source === 'marketplace'
    ? t('billing.page.checkout.os.catalog.search.placeholderAppsShort')
    : t('billing.page.checkout.os.catalog.search.placeholderOsShort')
})

defineShortcuts({
  '/': () => input.value?.inputRef?.focus(),
})

function onEscape(event: KeyboardEvent) {
  if (!props.modelValue) return
  event.preventDefault()
  event.stopPropagation()
  emit('update:modelValue', '')
}

const announcement = computed(() =>
  props.resultCount === null
    ? ''
    : t('billing.page.checkout.os.catalog.search.results', { count: props.resultCount }, props.resultCount)
)
</script>

<template>
  <div ref="wrapper">
    <UInput
      ref="input"
      :model-value="modelValue"
      data-testid="template-search"
      size="lg"
      icon="i-lucide-search"
      class="w-full"
      autocomplete="off"
      :placeholder="placeholder"
      :aria-label="label"
      @update:model-value="emit('update:modelValue', String($event ?? ''))"
      @keydown.esc="onEscape"
    >
      <template #trailing>
        <UButton
          v-if="modelValue"
          color="neutral"
          variant="link"
          size="xs"
          icon="i-lucide-x"
          :aria-label="$t('billing.page.checkout.os.clearSearch')"
          @click="emit('update:modelValue', '')"
        />
        <UKbd
          v-else
          value="/"
          class="hidden sm:inline-flex"
          :title="$t('billing.page.checkout.os.catalog.search.shortcut')"
        />
      </template>
    </UInput>
    <p class="sr-only" aria-live="polite">{{ announcement }}</p>
  </div>
</template>
